import {Translations} from '@aboutyou/i18n';
import {
  FeaturesProvider,
  UserSelectedDiscountProvider,
} from '@aboutyou/logic-components';
import {GlobalErrorBoundary} from '@aboutyou/mlm';
import {
  ErrorBoundary,
  ToastContextProvider,
  UIProvider,
} from '@aboutyou/ui-components';
import {UserStateProvider} from '@aboutyou/user';
import React, {FC} from 'react';
import {IconsCache} from 'react-lazy-svg';
import {ConditionalStyleSheetManager} from './ConditionalStyleSheetManager';
import {RealUserModeProvider} from './RealUserMode';

interface Props extends React.ComponentProps<typeof FeaturesProvider> {
  loadIconSVG: (key: string) => Promise<string | undefined>;
  knownIcons?: IconsCache;
  enableRealUserMode: boolean;
  authToken?: string;
}

export const BaseProvider: FC<Props> = ({
  children,
  headContext,
  loadIconSVG,
  knownIcons,
  i18nConfig,
  routerConfig,
  hostConfig,
  grpcConfig,
  storage,
  featureFlagSetup,
  documentReferrer,
  enableRealUserMode,
  enableBotMode,
  disableTranslationLoader,
  authToken,
}) => {
  const errorBoundaryTexts = getErrorBoundaryTranslations(
    i18nConfig.translations,
  );

  return (
    <RealUserModeProvider value={enableRealUserMode}>
      <GlobalErrorBoundary errorBoundaryTexts={errorBoundaryTexts}>
        <ConditionalStyleSheetManager isRTL={i18nConfig.htmlDir === 'rtl'}>
          <UIProvider loadIconSVG={loadIconSVG} knownIcons={knownIcons}>
            <FeaturesProvider
              disableTranslationLoader={disableTranslationLoader}
              enableBotMode={enableBotMode}
              documentReferrer={documentReferrer}
              storage={storage}
              headContext={headContext}
              hostConfig={hostConfig}
              i18nConfig={i18nConfig}
              routerConfig={routerConfig}
              grpcConfig={grpcConfig}
              featureFlagSetup={featureFlagSetup}
            >
              <ToastContextProvider maxToasts={1}>
                <UserStateProvider
                  UserSelectedDiscountProvider={UserSelectedDiscountProvider}
                  authToken={authToken}
                >
                  <ErrorBoundary renderContext="bootstrap_base_provider">
                    {children}
                  </ErrorBoundary>
                </UserStateProvider>
              </ToastContextProvider>
            </FeaturesProvider>
          </UIProvider>
        </ConditionalStyleSheetManager>
      </GlobalErrorBoundary>
    </RealUserModeProvider>
  );
};

const getErrorBoundaryTranslations = (translations: Partial<Translations>) => {
  const strings: Array<keyof Translations> = [
    'Oh, Sorry!',
    'Our shop is currently not available.',
    'Try again',
    'Start page',
  ];
  return strings.reduce(
    (keys, key) => ({...keys, [key]: translations[key] ?? key}),
    {} as Translations,
  );
};
